import React, { useId, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../../../redux/store";
import { getSubscriptionType } from "../../../../redux/slice/subscription/subscriptionSlice";
import { cn } from "lib/utils";
import { Spinner } from "../../../../components/ui/spinner";
import { Input } from "../../../../components/ui/input";
import { isFreeOfCharge } from "../../../../util/payments";
import { Button } from "../../../../components/ui/button";
import { useApplySubscriptionVoucher } from "../../../../redux/flows/applySubscriptionVoucherFlow";
import { useTaxStatement } from "../../../../util/taxStatement";
import { STATION_TIME_ZONE } from "util/stationTime";

/**
 * Renders decorative ticket hole punch effect on the side of the ticket
 */
const TicketHole = ({ side, uniqueId }: { side: "left" | "right"; uniqueId: string }) => {
    const clipPathId = `${side}Hole-${uniqueId}`;
    const isRight = side === "right";

    return (
        <svg 
			className={cn(
				"absolute top-[29%] w-4 h-4 z-10 pointer-events-none",
				isRight ? "right-[-8px]" : "left-[-8px]"
			)} 
			viewBox="0 0 16 16"
		>
			<defs>
				<clipPath id={clipPathId}>
					<rect x={isRight ? "0" : "8"} y="0" width="8" height="16" />
				</clipPath>
			</defs>
			{/* White rectangle to cover the border at the hole */}
			<rect x={isRight ? "5" : "7"} y="0" width="4" height="16" fill="white" />
			{/* Background fill without stroke to avoid double border */}
			<circle cx="8" cy="8" r="8" fill="rgb(249, 250, 251)" clipPath={`url(#${clipPathId})`} />
			{/* Only draw the outer arc border */}
			<path 
				d={isRight ? "M 8 0.5 A 7.5 7.5 0 0 0 8 15.5" : "M 8 0.5 A 7.5 7.5 0 0 1 8 15.5"} 
				stroke="rgba(0, 0, 0, 0.1)" 
				strokeWidth="1" 
				fill="none" 
				strokeLinecap="round" 
			/>
		</svg>
    );
};

const KeyHandoverSubscriptionSummaryStep = () => {
    const { t, i18n } = useTranslation();
    const taxStatement = useTaxStatement();
    const uniqueId = useId();
    const applyVoucher = useApplySubscriptionVoucher();

    const location = useSelector((state: RootState) => state.bookingCreation.selectedLocation);
    const selectedPlan = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
    );
    const hostData = useSelector((state: RootState) => state.subscription.newSubscription.hostData);
    const appliedVoucher = useSelector(
        (state: RootState) => state.subscription.newSubscription.appliedVoucher
    );
    const isApplyingVoucher = useSelector(
        (state: RootState) => state.subscription.newSubscription.loading.applyingVoucher
    );
    const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);

    const [voucherCode, setVoucherCode] = useState("");

    const isLoading = !location || !selectedPlan || !hostData;

    const subscriptionType = getSubscriptionType(selectedPlan);

    // Voucher validation - alphanumeric with optional dashes/underscores/spaces
    const isValidVoucher = useCallback((code: string) => {
        return /^[a-zA-Z0-9-_ ]+$/.test(code) && code.trim().length > 0;
    }, []);

    const isApplyButtonDisabled = useMemo(
        () => !isValidVoucher(voucherCode),
        [voucherCode, isValidVoucher]
    );

    const handleVoucherApply = useCallback(async () => {
        if (isApplyButtonDisabled || !selectedPlan?.id) return;

        await applyVoucher(selectedPlan.id, voucherCode.trim());

        setVoucherCode("");
    }, [applyVoucher, voucherCode, isApplyButtonDisabled, selectedPlan?.id]);

    // `free` is computed further down, after the loading guard; a voucher
    // cannot reduce a free plan, so the field is gated there too.
    const voucherAllowed = tenantConfig?.allowCoupons && !appliedVoucher;

    // Currency formatter - must be defined before early return
    const formatCurrency = useMemo(() => {
        return new Intl.NumberFormat(i18n.language || "de", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
        });
    }, [i18n.language]);

    if (isLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center py-10">
                <Spinner size="md" />
            </div>
        );
    }

    const originalPrice = selectedPlan?.price || 0;
    // A free plan has no price for a tax statement to be about. Tested on the
    // LIST price, not the post-voucher one: a paid plan a voucher reduces to
    // zero still had a price. ADR-0008 amendment 2026-09-16.
    // Reads the RAW plan price, not `originalPrice` above: that one is `|| 0`,
    // which turns "no plan selected" into a zero this rule would call free.
    const free = isFreeOfCharge(selectedPlan?.price, selectedPlan ? 1 : 0);
    const showVoucherButton = voucherAllowed && !free;

    // Use pre-calculated values from voucher API response
    const finalPrice = appliedVoucher ? appliedVoucher.newAmount : originalPrice;
    const discount = appliedVoucher ? appliedVoucher.discount : 0;

    const formattedOriginalPrice = formatCurrency.format(originalPrice);
    const formattedDiscount = formatCurrency.format(-discount);
    const formattedFinalPrice = formatCurrency.format(finalPrice);

    const billingPeriod =
        subscriptionType === "monthly"
            ? t("book_location.keys.subscription_plan.billed_monthly", {
                  defaultValue: "per month",
              })
            : t("book_location.keys.subscription_plan.billed_yearly", { defaultValue: "per year" });
    const planTitle =
        subscriptionType === "monthly"
            ? t("book_location.keys.subscription_plan.monthly_title", {
                  defaultValue: "Monthly Plan",
              })
            : t("book_location.keys.subscription_plan.yearly_title", {
                  defaultValue: "Annual Plan",
              });

    const textBoldClass = "text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words";

    return (
        <div className="flex flex-col h-full">
            <div className="overflow-auto flex-grow">
                <div className="relative w-full overflow-clip">
                    <TicketHole side="left" uniqueId={uniqueId} />
                    <TicketHole side="right" uniqueId={uniqueId} />

                    <div className="relative border border-foreground/10 rounded-lg bg-white shadow-sm p-6 space-y-4">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                                {t("book_location.location", { defaultValue: "Location" })}
                            </p>
                            <p className="text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words">
                                {location.name}
                            </p>
                        </div>

                        <div className="border-t border-foreground/10 my-4" />

                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                                {t("book_location.keys.subscription_summary.start_date", {
                                    defaultValue: "Start Date",
                                })}
                            </p>
                            <p className={textBoldClass}>
                                {new Date().toLocaleDateString(i18n.language, {
                                    timeZone: STATION_TIME_ZONE,
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </p>
                        </div>

                        <div className="border-t border-foreground/10 my-4" />

                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                                {t("book_location.keys.subscription_summary.subscription_plan", {
                                    defaultValue: "Subscription plan",
                                })}
                            </p>
                            <div className="flex justify-between items-center mb-2 gap-4">
                                <div className="min-w-0">
                                    <p className="text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words">
                                        {planTitle}
                                    </p>
                                    <p className="text-sm text-foreground/70">{billingPeriod}</p>
                                </div>
                                <p className="text-lg font-bold text-foreground/80 flex-shrink-0">
                                    {formattedOriginalPrice}
                                </p>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-foreground/10 my-4" />

                        {/* Voucher Code Input */}
                        {showVoucherButton && (
                            <div>
                                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                                    {t("general.enter_coupon_code", {
                                        defaultValue: "Voucher Code",
                                    })}
                                </p>
                                <div className="flex flex-row justify-between items-center gap-4">
                                    <Input
                                        type="text"
                                        placeholder={t("general.enter_coupon_code", {
                                            defaultValue: "Enter voucher code",
                                        })}
                                        value={voucherCode}
                                        onChange={(e) => setVoucherCode(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                void handleVoucherApply();
                                            }
                                        }}
                                        inputSize="sm"
                                        className="w-full"
                                        aria-label={t("general.enter_coupon_code")}
                                    />
                                    <Button
                                        onClick={handleVoucherApply}
                                        disabled={isApplyButtonDisabled}
                                        variant="primary"
                                        size="sm"
                                        aria-busy={isApplyingVoucher}
                                        className={cn(
                                            "flex-shrink-0 min-w-[80px]",
                                            isApplyingVoucher && "pointer-events-none"
                                        )}
                                    >
                                        {isApplyingVoucher ? (
                                            <Spinner
                                                size="sm"
                                                className="!border-slate-900 !border-t-transparent"
                                            />
                                        ) : (
                                            t("orders.ticket_overview.button", {
                                                defaultValue: "Apply",
                                            })
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Divider */}
                        <div className="border-t border-foreground/10 my-4" />

                        {/* Subscriber Information */}
                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
                                {t(
                                    "book_location.keys.subscription_summary.subscriber_information",
                                    {
                                        defaultValue: "Subscriber information",
                                    }
                                )}
                            </p>
                            <div className="space-y-2.5">
                                <div className="flex justify-between items-center gap-4">
                                    <span className="text-sm text-foreground/70 flex-shrink-0">
                                        {t("book_location.personal_data.first_name", {
                                            defaultValue: "First name",
                                        })}
                                    </span>
                                    <span className="text-sm font-medium text-foreground/80 text-right break-words">
                                        {hostData.firstName}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center gap-4">
                                    <span className="text-sm text-foreground/70 flex-shrink-0">
                                        {t("book_location.personal_data.last_name", {
                                            defaultValue: "Last name",
                                        })}
                                    </span>
                                    <span className="text-sm font-medium text-foreground/80 text-right break-words">
                                        {hostData.lastName}
                                    </span>
                                </div>
                                <div className="flex justify-between items-start gap-4">
                                    <span className="text-sm text-foreground/70 flex-shrink-0">
                                        {t("book_location.personal_data.email", {
                                            defaultValue: "Email",
                                        })}
                                    </span>
                                    <span className="text-sm font-medium text-foreground/80 text-right break-all">
                                        {hostData.email}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center gap-4">
                                    <span className="text-sm text-foreground/70 flex-shrink-0">
                                        {t("book_location.personal_data.phone_number", {
                                            defaultValue: "Phone number",
                                        })}
                                    </span>
                                    <span className="text-sm font-medium text-foreground/80 text-right break-words">
                                        {hostData.phone}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-foreground/10 my-4" />

                        {/* Price Breakdown */}
                        <div className="space-y-2">
                            {discount > 0 && (
                                <>
                                    <div className="flex justify-between items-center gap-4">
                                        <p className="text-sm text-foreground/70">
                                            {t("book_location.booking_summary.subtotal", {
                                                defaultValue: "Subtotal",
                                            })}
                                        </p>
                                        <p className="text-sm text-foreground/70 line-through">
                                            {formattedOriginalPrice}
                                        </p>
                                    </div>
                                    <div className="flex justify-between items-center gap-4">
                                        <p className="text-sm text-green-600">
                                            {t("book_location.booking_summary.discount", {
                                                defaultValue: "Discount",
                                            })}{" "}
                                            {appliedVoucher && `(${appliedVoucher.code})`}
                                        </p>
                                        <p className="text-sm text-green-600">
                                            {formattedDiscount}
                                        </p>
                                    </div>
                                </>
                            )}
                            <div className="flex justify-between items-center gap-4">
                                <p className="text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words">
                                    {t("book_location.confirmation.total_items", {
                                        defaultValue: "Total",
                                    })}
                                </p>
                                <p className="text-lg font-bold text-foreground/80 flex-shrink-0">
                                    {!isApplyingVoucher ? formattedFinalPrice : "..."}
                                </p>
                            </div>
                        </div>

                        {!free && (
                            <p className="text-sm text-foreground/60">
                                {taxStatement.sentence}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(KeyHandoverSubscriptionSummaryStep);
